/**
 * English locale. This file doubles as the key catalog: any key present here
 * must exist in fr.ts (key parity is required by i18n parity discipline) and
 * English values must match the original hardcoded strings so tests and
 * screenshots stay byte-stable.
 */
const en = {
  // Library UX fixes (scope A, dedicated block): keys introduced by the
  // Library-state/search/paginate fixes PR.
  libraryUX: {
    periodFilter: 'Period',
    periodAll: 'All periods',
    period30d: 'Past 30 days',
    period90d: 'Past 3 months',
    period180d: 'Past 6 months',
    period365d: 'Past year',
    showMoreAttempts_one: 'Show {{count}} more attempt',
    showMoreAttempts_other: 'Show {{count}} more attempts',
    loadMoreSessions: 'Load more sessions',
    sessionBadge: 'Session',
    sessionVideos_one: '{{count}} video',
    sessionVideos_other: '{{count}} videos',
    sessionRowAria_one: 'Training session · {{count}} video',
    sessionRowAria_other: 'Training session · {{count}} videos',
  },
  room: {"soundOn": 'Turn sound on',"next": "Next","toResults": "See results","waiting": "Waiting for the others…","joinCode": "Room code","joinGo": "Join","shadow": "My shadow","playerCount_one": "{{count}} player", "playerCount_other": "{{count}} players", "ghostCount_one": "{{count}} ghost", "ghostCount_other": "{{count}} ghosts", "diveCount_one": "{{count}} dive", "diveCount_other": "{{count}} dives","guessCode": 'Guess the Code',"harsh": 'The harshest',"shadowOptIn": 'Allow my shadow',"title": "Judge Room", "create": "Create a room", "room": "Room {{code}}", "invite": "{{name}} invites you to judge", "nickname": "Your nickname", "join": "Join room", "account": "I have an account", "error": "Connection or action failed. Try again.", "copy": "Copy", "copied": "Copied", "jury": "Panel", "host": "host", "ghost": "ghost", "player": "player", "mode": "Mode", "classic": "Classic", "mole": "🕵️ The mole", "dives": "Dives", "latest": "My latest session", "community": "Community", "code": "By code", "apply": "Choose", "empty": "No public dives ready to play.", "start": "Start", "secret": "🕵️ You are the mole. Push the scores without getting caught.", "submitted": "Score submitted", "submit": "Submit", "high": "HIGH", "low": "LOW", "juryScore": "Panel score", "yourScore": "Your score", "points": "Points", "roast": "🌶️ The panel has spoken.", "findMole": "Is there a mole?", "nobody": "Nobody", "vote": "Vote", "finished": "Game over", "moleWas": "The mole: {{name}}", "sharp": "Eagle eye", "streak": "Streak", "challenge": "Challenge other friends", "replay": "Play again", "line": {"shadow": 'Watching the entry.',"mum": "Such a hard worker.", "coach": "Splash. Again.", "copy": "Taking notes.", "olympian": "Almost Olympic."}},
  series: { mySeries: "My series" },
  messages: {"title": "Messages", "message": "Message", "new": "New message", "recipient": "Choose a member", "inbox": "Inbox", "empty": "No conversations yet.", "choose": "Choose a conversation or start a new one.", "conversation": "Conversation", "begin": "Start the conversation.", "older": "Older messages", "more": "More conversations", "compose": "Your message", "placeholder": "Write a message… Use @ to mention a member.", "send": "Send", "sent": "Sent", "delivered": "Delivered", "readAt": "Read at {{time}}", "react": "React with a heart", "reactionHint": "Double tap a message to react. Use the heart button with a keyboard.", "yourMessage": "Your message", "receivedMessage": "Received message", "watch": "Watch dive", "diveCard": "Dive", "diveUnavailable": "This dive is no longer available to you.", "sendDive": "Send dive in a message", "diveSent": "Dive sent.", "open": "Open conversation", "sendDiveError": "Could not send. The dive must be available to both members. Try again.", "error": "Messages unavailable. Try again.", "newShort": "New", "placeholderShort": "Message…", "you": "You:", "today": "Today", "yesterday": "Yesterday", "readShort": "Read {{time}}", "unread_one": "{{count}} unread message", "unread_other": "{{count}} unread messages", "newGroup": "New group", "createGroup": "Create group · {{count}} people", "pickTwo": "Pick at least 2 people", "groupFull": "Groups hold up to {{count}} people", "removeMember": "Remove {{name}}", "members_one": "{{count}} member", "members_other": "{{count}} members", "groupOptions": "Group options", "leave": "Leave group", "leaveConfirm": "Leave this group? You will no longer receive its messages.", "readBy_one": "Read by {{count}}", "readBy_other": "Read by {{count}}", "formerMember": "Former member", "create": "Create", "groupName": "Group name", "groupNamePlaceholder": "Group name (optional)", "saveName": "Save name", "notificationDesc": "Private message alerts"},
    mentions: { choose: 'Mention a member', loading: 'Finding members…', empty: 'No members found', error: 'Could not find members.' },
  common: {
    loading: 'Loading…',
    loadingPage: 'Loading page…',
    optional: 'optional',
    characters: 'characters',
    close: 'Close',
    back: 'Back',
    save: 'Save',
    cancel: 'Cancel',
    error: 'Error',
    retry: 'Retry',
  },
  journey: {
    title: 'Your Diving Journey',
    errorLoad: 'Failed to load your journey',
    uniqueDives: 'Unique Dives',
    trainingDays: 'Training Days',
    improvement: 'Improvement',
    trajectory: 'Trajectory',
    sectionsAria: 'Journey sections',
    section: 'Journey section',
    yourStory: 'Your Story',
    ddProgression: 'DD Progression',
    milestones: 'Milestones',
    phaseAnalysis: 'Phase Analysis',
    psychology: 'Psychology',
    patterns: 'Patterns',
    show: 'Show',
    all1plus: 'All (1+)',
    only5: '5 only',
    twoPhaseAnalysis: 'Two-Phase Dive Analysis',
    twoPhaseFormat: 'Two-Phase Format',
    legacyFormat: 'Legacy Format',
    newFormatPct: 'New Format %',
    phase1Title: 'Phase 1: Hurdle + Flight (Takeoff)',
    phase2Title: 'Phase 2: Opening + Entry (Landing)',
    averageScore: 'Average Score',
    commonCombinations: 'Most Common Phase Combinations',
    coachingInsights: 'Coaching Insights',
    mentalBlockStatus: 'Mental Block Status',
    psychologicalProfile: 'Psychological Profile',
    mentalBlocksDetected: 'Mental Blocks Detected',
    fearVsDifficulty: 'Fear vs Difficulty',
    confidenceTrend: 'Confidence Trend',
    stable: 'Stable',
    mentalBlockDetected: 'Mental Block Detected',
    mentalBlockDetail: 'Same max difficulty for 3+ months.',
    growth: 'Growth',
    firstMonthAvg: 'First Month Avg Performance',
    latestMonthAvg: 'Latest Month Avg Performance',
    overallImprovement: 'Overall Improvement',
    noPsychology: 'No psychology data.',
    performanceProgression: 'Performance Progression',
    totalImprovement: 'Total Improvement',
    timeline: 'Timeline',
    trainingSpan: 'Training Span',
    days: 'days',
    started: 'Started',
    latest: 'Latest',
    differentDives: 'different dives',
  },
  milestones: {
    labels: {
      first_attempt: 'First attempt',
      first_success: 'First success',
      first_good: 'First Good',
      first_perfect: 'First Perfect',
      first_from_height: 'First dive from height',
      breakthrough: 'Breakthrough',
      redemption: 'Redemption',
      comeback: 'Comeback',
      avoided_dive_attempted: 'Facing the fear',
      success_streak: 'Success streak',
      perfect_streak: 'Perfect streak',
      consistency_achieved: 'Consistency',
      plateau_broken: 'Plateau broken',
      new_height_conquered: 'New height conquered',
      training_streak: 'Training streak',
      synchro_first_success: 'First SYNC success',
      personal_best: 'Personal best',
    },
    historyAria: 'Milestone history',
    noMatches: 'No milestones match this filter.',
    count: 'milestones',
    majorNotable: '{{count}} major or notable',
    majorBreakthrough: 'Major breakthrough',
    notableStep: 'Notable step',
    meaningfulProgress: 'Meaningful progress',
    recordedProgress: 'Recorded progress',
    review: 'Review',
    dismiss: 'Dismiss milestone',
    afterFailures: 'This came after {{count}} previous attempts. Persistence paid off.',
    firstTryPerfect: 'First try perfection!',
    masteryAfter: 'After {{count}} attempts, mastery.',
    sameDay: 'Same day.',
    daysLater: '{{count}} days later.',
    first_attempt: { title: 'First {{code}} from {{height}}', narrative: 'You attempted {{code}} from {{height}} for the first time. Every master was once a beginner.' },
    first_success: { title: 'First successful {{code}}', narrative: 'Your first successful {{code}} from {{height}}!' },
    first_good: { title: 'First Good {{code}}', narrative: 'First Good performance of {{code}} from {{height}}.' },
    first_perfect: { title: 'First Perfect {{code}}', narrative: 'Perfect execution of {{code}} from {{height}}.' },
    first_from_height: { title: 'First dive from {{height}}', narrative: 'You dove from {{height}} for the first time.' },
    breakthrough: { title: 'Breakthrough on {{code}}', narrative: 'You landed {{code}} after {{failures}} consecutive failed attempts. This is what growth looks like.' },
    redemption: { title: 'Redemption: Perfect {{code}}', narrative: 'From belly flop to perfection.' },
    comeback: { title: 'Comeback: {{code}}', narrative: 'You returned to {{code}} after {{days}} days away.' },
    avoided_dive_attempted: { title: 'Facing the fear: {{code}}', narrative: 'You faced {{code}} again after {{days}} days away, having struggled with it before.' },
    success_streak: { title: '{{streak}}x Success Streak', narrative: '{{streak}} successful {{code}}s in a row from {{height}}.' },
    perfect_streak: { title: '{{streak}}x Perfect Streak', narrative: '{{streak}} consecutive Perfect executions of {{code}}.' },
    consistency_achieved: { title: 'Consistency achieved: {{code}}', narrative: 'Your last 10 {{code}}s from {{height}} show remarkable consistency. This dive is competition-ready.' },
    plateau_broken: { title: 'Plateau broken', narrative: 'After months of steady performance, you broke through.' },
    new_height_conquered: { title: 'New height conquered: {{height}}', narrative: 'You conquered {{height}}.' },
    training_streak: { title: '{{streak}} days in a row', narrative: '{{streak}} consecutive days of training.' },
    synchro_first_success: { title: 'First successful SYNC dive', narrative: 'Your first successful synchronized dive.' },
    personal_best: { title: 'Personal best: {{code}}', narrative: 'New personal best on {{code}} from {{height}}.' },
  },
  clipViewer: {
    diveReview: 'DIVE REVIEW',
    noLinkedClip: 'No linked clip for this attempt.',
    likeDive: 'Like dive',
    like: 'Like',
  comments: 'Comments',
    analysis: 'Analysis',
    private: 'Private',
    public: 'Public',
    defaultCaption: 'Every dive, a little further.',
    details: 'Details',
    closeDetailsAndComments: 'Close details and comments',
    diveDetails: 'Dive details',
    discussion: 'DISCUSSION',
    theAttempt: 'THE ATTEMPT',
    closePanel: 'Close panel',
    description: 'DESCRIPTION',
    noDescription: 'No description for this attempt.',
    privateDive: 'Private dive',
    publicDive: 'Public dive',
    manageDive: 'Manage dive',
    playPause: 'Play or pause',
    prevAttempt: 'Previous attempt',
    nextAttempt: 'Next attempt',
    play: 'Play video',
    pause: 'Pause video',
    resume: 'Resume playback',
    unmute: 'Unmute video',
    mute: 'Mute video',
    videoPosition: 'Video position',
    shareDive: 'Share dive',
    copyLinkToShare: 'Copy this link to share the video:',
    shareLink: 'Share link',
    visibility: 'Dive visibility',
    visibilityHint: 'Public: signed-in divers. Private: only you.',
    linkCopied: 'Link copied',
    linksRevoked: 'Shared links revoked',
    createLinkError: 'Could not create a link. Try again.',
    revokeError: 'Could not revoke links. Try again.',
    sharing: 'Sharing…',
    share: 'Share',
    videoSharing: 'Video sharing',
    shareVideo: 'Share video',
    dismissSharing: 'Dismiss sharing notification',
    copyLink: 'Copy link',
    shareChoose: 'Choose how to share this dive.',
    shareLinkHint: 'Share outside the app',
    shareDm: 'Send in a message',
    shareDmHint: 'Choose a diver to send this clip to.',
    revokeLinks: 'Revoke shared links',
    couldNotSave: 'Could not save.',
    tabEvaluation: 'Evaluation',
    toRate: 'to rate',
    sameDay: 'Dives from the same day',
    openFromStrip: 'Open {{code}}, score {{score}}',
  },
  clipEvaluation: {
    yourScore: 'Your score',
    ownerTitle: 'Scores received',
    circuitRequest: 'Requested evaluation',
    circuitCommunity: 'Community score · provisional',
    prompt: 'Note from the diver:',
    commentPlaceholder: 'Comment for the diver (optional)',
    confirm: 'Confirm my score',
    confirmScore: 'Confirm {{score}}',
    once: 'One score per dive. Other judges’ scores appear after yours.',
    submitError: 'Could not save your score. Try again.',
    loadError: 'Could not load the evaluation.',
    mean: 'average · {{count}} judge(s)',
    juryAria: 'Jury scores',
    noVotes: 'No scores yet. The {{score}} shown is the logged performance score, not a jury score.',
    closed: 'This dive is not open to scores.',
    ownerPrivate: 'This dive is private: request an evaluation to receive scores.',
    ownerCannotRate: 'You cannot score your own dive.',
    excluded: 'Excluded from automatic selections.',
  },
  comments: {
    viewProfileBy: 'View {author}’s profile',
    errorLoad: 'Could not load comments.',
    errorLoadReplies: 'Could not load replies. Try again.',
    errorSaveReply: 'Could not save your change. Try again.',
    errorPost: 'Could not post your comment. Your text is saved here.',
    join: 'Join the discussion',
    placeholder: 'Add a comment…',
    post: 'Post comment',
    loading: 'Loading comments…',
    noComments: 'No comments yet.',
    beFirst: 'Be the first to share a thought on this dive.',
    replyPlaceholder: 'Write a reply…',
    savingReplies: 'Saving or loading replies…',
    olderReplies: 'Older replies',
    replyTo: 'Reply to',
    postReplyTo: 'Post reply to {{author}}',
    reply: 'Reply',
    likeBy: 'Like comment by {{author}}',
    deleteBy: 'Delete comment by {{author}}',
    retryReplies: 'Retry replies',
    hideReplies: 'Hide replies',
    viewReplies: 'View replies ({{count}})',
  },
  diveManagement: {
    title: 'Edit dive',
    saved: 'Dive saved.',
    errorSave: 'The dive could not be saved. Please try again.',
    errorDelete: 'The dive could not be deleted. Please try again.',
    performance: 'Dive performance',
    personalNotes: 'Personal notes',
    working: 'Working…',
    saveChanges: 'Save changes',
    delete: 'Delete dive',
    deleteConfirm: 'Delete this dive record? This cannot be undone. The original video is retained.',
    confirmDelete: 'Confirm deletion',
  },
  sync: {
    statusProgress: 'Sync {{status}}',
    synced: 'Synced',
    errors: 'Errors',
    duration: 'Duration',
    recentMilestones: 'Recent milestones',
    started: 'Sync Started',
    complete: 'Sync Complete',
    error: 'Sync Error',
    failed: 'Sync failed',
    videosCount: '{{count}} videos',
    videosSynced: '{{count}} videos synced',
    milestoneToast: 'Milestone',
    dismissNotification: 'Dismiss notification',
    dismissError: 'Dismiss error',
    connected: 'Connected',
    offline: 'Offline',
    disconnected: 'Disconnected',
    reconnecting: 'Reconnecting…',
    inProgress: 'Sync in Progress',
    idLabel: 'ID:',
    syncId: 'Sync ID',
    errorMessage: 'Error Message',
    retryAttempts: 'Retry Attempts:',
    retry: 'Retry',
    dismiss: 'Dismiss',
    maxRetries: 'Maximum retries reached.',
    unknownError: 'Unknown error occurred',
  },
  thumbnail: {
    posterFor: 'Poster for {{code}}',
    ready: 'Video ready',
    linked: 'Video linked',
  },
  mediaStatus: {
    ready: 'ready',
    partial: 'partial',
    missing: 'missing',
    failed: 'failed',
    unavailable: 'unavailable',
    queued: 'queued',
    waiting_for_mac: 'waiting for mac',
    claimed: 'claimed',
    materializing: 'materializing',
    exporting: 'exporting',
    transcoding: 'transcoding',
    uploading: 'uploading',
    verifying: 'verifying',
    photos: { ok: 'ok', permission_required: 'permission required', denied: 'denied', unknown: 'unknown' },
    not_cached: 'not cached',
    replacement_available: 'replacement available',
    replacement_exhausted: 'replacement exhausted',
  },
  progress: {
    noRecentSessions: 'No sessions in the last 7 days',
    showing: 'Showing {{range}}',
    mostRecentWeek: 'Most recent week',
    last7Days: 'Last 7 days',
    totalDives: 'Total Dives',
    vsPrev: 'vs prev',
    vsPrevWeek: 'vs prev week',
    avgPerformance: 'Avg Performance /10',
    calibratedScore: 'Calibrated score',
    success: 'Success',
    latest: 'Latest',
    sessions: 'Sessions',
    recentSessions: 'Recent Sessions',
    performance: 'Performance /10',
    successRate: 'Success Rate',
    divesCount: '{{count}} dives',
  },
  sharedDive: {
    eyebrow: 'SHARED DIVE',
    errorInvalid: 'This shared link is invalid or no longer available.',
    errorUnavailable: 'This shared video is unavailable. The link may have been revoked.',
    like: 'Like',
    commentsLabel: 'Comments',
    videoAria: 'Shared video',
    videoUnavailable: 'Video unavailable.',
    retryVideo: 'Retry video',
    discussionAria: 'Guest discussion',
    commentingAs: "You’re commenting as",
    loadingShared: 'Loading shared video…',
  },
  sessionVideo: {
    title: 'Session videos',
    empty: 'No session video yet.',
    loading: 'Loading session…',
    unavailable: 'This session video is unavailable.',
    invalidLink: 'This shared link is invalid or no longer available.',
    unplayable: 'This video doesn’t play here',
    download: 'Download · {{size}}',
    share: 'Share session',
    videoAria: 'Session video',
  },
  repMedia: {
    viability: {
      ready: 'already ready',
      previously_successful: 'previously materialized',
      untested: 'untested',
      retryable_failure: 'retryable failure',
      known_placeholder_failure: 'placeholder failure',
      permanently_unavailable: 'permanently unavailable',
    },
    role: { low: 'Low', median: 'Typical', high: 'High' },
    mediaVideoReady: 'Video ready',
    mediaPosterOnly: 'Poster ready · video missing',
    mediaUnavailable: 'Video unavailable',
    mediaMissing: 'Media missing',
    posterAlt: '{{code}} {{label}} representative poster',
    play: 'Play',
    loadCandidatesError: 'Unable to load replacement candidates.',
    summaryDiveCodes: 'Dive codes',
    summaryRepresentatives: 'Representatives',
    summaryFullyReady: 'Fully ready codes',
    summaryPartial: 'Partial codes',
    summaryZeroReady: 'Zero-ready codes',
    auditZeroReady: 'Zero-ready audit',
    auditPlaceholder: 'Placeholder failures',
    auditViable: 'Viable zero-ready candidates',
    auditPhotos: 'Photos access',
    photosUnknown: 'unknown',
    filterDiveCode: 'Dive code',
    filterReadiness: 'Readiness',
    all: 'All',
    readinessReady: 'Ready',
    readinessPartial: 'Partial',
    readinessReplacementAvailable: 'Replacement available',
    readinessReplacementExhausted: 'Replacement exhausted',
    readinessMissing: 'Missing',
    filterRole: 'Role',
    filterHeight: 'Height',
    filterScope: 'Diver scope',
    scopeSelf: 'My dives',
    scopeAll: 'All divers',
    scopeSelected: 'Selected diver ID',
    filterDiverId: 'Diver ID',
    filterSort: 'Sort',
    sortNeedsCoverage: 'Needs coverage',
    sortFullyReady: 'Fully ready',
    sortRecentlyUpdated: 'Recently updated',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    coverageLine: 'Poster {{poster}}/{{total}} · Video {{video}}/{{total}}',
    coverageIncomplete: 'Coverage incomplete',
    noRepresentative: 'No {{role}} representative selected.',
    noMatches: 'No matches.',
    candidatesAria: 'Replacement candidates',
    candidates: 'Replacement candidates',
    noCandidates: 'No unused candidates.',
    scoreDistance: 'Score distance',
    useWithoutPreparing: 'Use without preparing',
    useAndPrepare: 'Use and prepare missing',
    findOrRetry: 'Find a replacement, or retry once the clip downloads in Photos on the Mac.',
    checkPermissions: 'Check Photos permissions on the Mac, then retry.',
    prepareOne: 'Prepare one',
    alreadyReady: '{{renditions}} already ready',
    noCachedRendition: 'No cached rendition',
    heightMatch: 'Height match',
    heightDiffers: 'Height differs',
    modifiersMatch: 'Modifiers match',
    modifiersDiffer: 'Modifiers differ',
    poster: 'Poster',
    proxy: 'Proxy',
  },
  whatsNew: {
    title: "What's new",
    buttonAria: "What's new",
    unread: 'Unread updates',
    close: 'Close update details',
    dismissHint: 'Dismiss update notification',
    updateHeadline: 'Your library just got an update',
    improvementsReady: '{{count}} improvements are ready to explore.',
    seeWhatsNew: "See what's new →",
    newerVersion: 'A newer version is available',
    reloadHint: "Reload when you're ready to see the latest improvements.",
    reloadApp: 'Reload app',
    latestUpdate: 'Latest update',
    noNotes: 'No update notes are available yet.',
    gotIt: 'Got it, back to my dives',
    version: 'Version {{version}}',
  },
  notifications: {
    title: 'Notifications',
    heading: 'Notifications',
    headingDesc: 'New videos, comments and milestones land here.',
    openAria: 'Open notifications',
    openAriaUnread: 'Open notifications, {{count}} unread',
    filterAria: 'Notification filter',
    showAll: 'Show all',
    unreadOnly: 'Unread ({{count}})',
    markRead: 'Mark as read',
    markAllRead: 'Mark all as read',
    saving: 'Saving…',
    openDive: 'Open dive',
    openSession: 'Open session',
    loading: 'Loading notifications…',
    emptyAll: 'No notifications yet.',
    emptyUnread: 'You are all caught up.',
    emptyHint: 'New videos and comments will appear here.',
    errorLoad: 'Notifications could not be loaded.',
    errorAction: 'The change could not be saved. Try again.',
    unreadOnlyPending: 'Unread',
    loadMore: 'Show older notifications',
    diveDate: 'Dive of {{date}}',
    listAria: 'Notifications',
    unreadBadge: 'Unread',
    types: {
      new_video: 'New video',
      video_ready: 'Video ready',
      new_comment: 'New comment',
      direct_message: "Messages",
      comment_reply: 'Comment reply',
      milestone: 'Milestone',
      sync_error: 'Sync issue',
      guest_activity: 'Guest activity',
      calendar_invitation: 'Calendar invitation',
      calendar_change: 'Calendar change',
      calendar_reminder: 'Calendar reminder',
      calendar_group_reminder: 'Group session reminder',
      calendar_follow: 'Followed calendar',
    },
    calendar: {
      invitation: 'You are invited',
      change: 'Event changed',
      cancellation: 'Cancelled',
      reminder: 'Tomorrow',
      new_event: 'New event',
      moved: 'Moved: {{from}} → {{to}}',
      venueChanged: 'Place changed: {{venue}}',
      venueChangedNoName: 'Place changed',
      sessionCancelled: 'Session cancelled: {{when}}',
      eventCancelled: 'This event is cancelled.',
      more: '+{{count}} more sessions',
      openEvent: 'Open event',
      redacted: 'This calendar notification is no longer available to you.',
    },
  },
  downloads: {
    saveFailed: 'Saving the batch could not finish',
    automaticError: 'Automatic downloading is unavailable here. Tap each file below to download it.',
    download: "Download video",
    short: "Download",
    requested: "Download requested",
    close: "Close downloads",
    retry: "Download again",
    deviceHint: "Check Downloads or Files on your device. If nothing starts, tap Download again.",
    preparing: "Preparing video…",
    preparePhotos: "Prepare video for Photos / sharing",
    photos: "Save to Photos / share",
    photosError: "Video sharing is unavailable here. You can download the file and save it to Photos from Files.",
    saved: "Clips saved to your library",
    saving: "Saving your clips…",
    progress: "{{count}} of {{total}} downloads requested",
    batchHint: "Your clips are in your library. Allow multiple downloads if your browser asks. If a file does not download, tap it below to retry.",
    saveError: "We could not finish saving this batch. Retry to continue; clips already saved stay in your library.",
    tryAgain: "Try again",
    saveDownload: "Save & download all clips",
    claimReady: "Your {{count}} clip(s) will be accepted into your library and downloaded to this device.",
  },
  claim: {
    title: 'Pick up your dives',
    ready: 'You have {{count}} clip(s) to review.',
    cta: 'Pick up my dives',
    signingIn: 'Signing you in…',
    invalid: 'This link is invalid or has already been used.',
    backToLogin: 'Back to sign in',
    error: 'The link could not be redeemed. Try again.',
  },
  deposits: {
    pendingTitle: 'Pending dive batches',
    pendingSubtitle: 'Review deposited clips and track acceptance. Each diver accepts their own dives.',
    awaitingOwner: 'Awaiting acceptance by {{name}}',
    readyForYou: 'Ready for you to accept',

    title: 'Pick up your dives',
    titleBatch: '{{name}} — {{count}} clip(s) to review',
    subtitle: 'Accept clips one by one — or all at once — and fix the code, height or your personal note along the way.',
    empty: 'Nothing to pick up. New deposited clips will appear here.',
    batchRow: '{{name}} — {{count}} clip(s)',
    acceptAll: 'Accept all',
    acceptSelected: 'Accept selected ({{count}})',
    selectAll: 'Select all',
    unselectAll: 'Unselect all',
    selectClip: 'Select clip {{code}}',
    depositedBy: 'Deposited by {{name}}',
    yourNote: 'Your note:',
    watch: 'Watch',
    edit: 'Edit',
    code: 'Dive code',
    height: 'Height',
    note: 'Personal note',
    save: 'Save',
    cancel: 'Cancel',
    allDone: 'All caught up!',
    backToLibrary: 'Back to the library',
    errorLoad: 'Pending clips could not be loaded.',
    errorAction: 'The change could not be saved. Try again.',
    banner: '{{count}} new clip(s) to pick up',
    bannerCta: 'Review',
    emailToggle: 'Batch emails',
    emailToggleDesc: 'One email per batch of deposited clips, with a sign-in link',
  },
  pdv: {
    executionLabel: 'Execution',
    signals: {
      technical: 'Technical magnitude',
      sameDiveResult: 'Same-dive result',
      breakthrough: 'Breakthrough',
      progression: 'Progression',
      consistency: 'Consistency',
      novelty: 'Novelty',
    },
    levels: { high: 'High', medium: 'Medium', low: 'Low', undefined: 'Unavailable' },
    emergingConfidence: 'Emerging · {{count}} prior same-dive attempts',
    unavailable: 'Unavailable',
    levelConfidence: '{{level}} confidence · {{count}} prior attempts',
    noPatterns: 'No earlier same-dive sequence to compare.',
    patternsAria: 'Historical pattern matches',
    closestPattern: 'Closest previous pattern',
    historicalMatch: 'Historical match',
    earlier: 'Earlier',
    vsCurrent: 'vs current',
    titleUnavailable: 'Personal Dive Value unavailable',
    titleError: 'Personal Dive Value could not be loaded',
    excluded: 'Excluded for this attempt (e.g. straight jump or lineup).',
    noOfficialDD: 'Official DD is not defined for this attempt.',
    version: 'Version {{version}}',
    provisional: 'Provisional: not enough same-dive history.',
    sameDivePercentile: '{{percentile}}th same-dive percentile. ',
    strongestContribution: '{{signal}} is the strongest contribution.',
    historicalContext: 'Historical context',
    emerging: 'Emerging',
    primarySignal: 'Primary signal',
    confidence: 'Confidence',
    hideDetails: 'Hide details',
    showDetails: 'Show details',
    whatContributed: 'What contributed',
    basedOn: 'Based on {{count}} earlier same-dive attempts{{percentile}}.',
    percentileSuffix: ' · {{percentile}}th percentile',
    similarSequences: 'Similar prior sequences',
    formula: 'Formula {{version}} · calculated from attempts before {{cutoff}}.',
  },
  addVideos: {
    qualityHint: 'Original files are kept. Playback keeps their resolution; HDR uploads get an SDR playback copy. Maximum: 1 GB, 3 minutes, 4K.',
    selectVideos: 'Select videos',
    add: 'Add videos',
    noticeAdded: '{{count}} video(s) added to your library.',
    errorAdd: 'The video could not be added. Your clip is kept here; try again.',
    sizeNotice: 'Select non-empty videos smaller than 1 GB each.',
    confirmStop: 'Stop adding videos? Saved clips remain in your library; remaining clips will be discarded.',
    videoN: 'Video {{index}} / {{total}}',
    addedAs: 'Added as {{name}}. Check the date: your browser supplies the file modification date.',
    diver: 'Diver',
    myDiverN: 'My diver #{{id}}',
    code: 'Code',
    height: 'Height',
    rating: 'Rating',
    date: 'Date',
    notes: 'Notes',
    visibleToCommunity: 'Visible to the community',
    evaluationRequest: 'Request community evaluation',
    evaluationPrompt: 'Comment for the jury',
    evaluationPromptPlaceholder: 'What should judges focus on?',
    uploading: 'Uploading… {{progress}}%',
    preparing: 'Preparing video… Keep this page open.',
    skip: 'Skip this clip',
    saveAndFinish: 'Save and finish',
    saveAndNext: 'Save and next',
    adding: 'Adding…',
  },
  releaseNotes: {
    "evaluations-today-hub": {
      "title": "Evaluations drop you straight into today's dives",
      "body": "Open Evaluations and today's selection starts right away, picking up where you left off. When you're done, your score cards land on the judges' table next to the rest of the panel, with your judging style, your daily streak and a countdown to the next selection. My dives becomes a scoreboard with a simple selection switch, and History groups every vote by day."
    },
    "evaluations-french": {
      "title": "Evaluations speak French throughout",
      "body": "French instructions, score cards and judging summaries now use consistent wording and decimal commas. The Evaluations announcement is also available in French."
    }
  },
  evaluations: {
    juryArea: 'Judging area',
    judgeSection: 'Judge',
    pausedTitle: 'Paused · {{count}} left today',
    pausedResume: 'Back to the board',
    ripTitle: 'Rip entry. ✓',
    ripNote: 'No splash, nothing left: {{count}} dive(s) judged today.',
    emptyPoolTitle: 'The pool is empty',
    emptyPoolNote: 'No public dive is waiting for you right now.',
    streak: '{{count}}-day streak',
    streakHint: 'Days in a row with at least one daily-selection vote',
    tableAria: 'Your score cards today',
    firstJudge: 'First judge',
    panelAlone: 'No other judge yet — you set the bar.',
    panelChip: 'Panel {{mean}}',
    persona: {
      ice: 'The Ice Judge 🧊',
      hard: 'Hard marker',
      spot: 'Spot-on 🎯',
      generous: 'Generous judge',
      santa: 'Santa Claus 🎅',
      unknown: 'Judging style: still warming up',
    },
    meterAria: '{{bias}} points versus the rest of the panel',
    meterHarsh: 'Harsh',
    meterGenerous: 'Generous',
    meterAbove: '{{delta}} above the other judges on average, over {{count}} dive(s).',
    meterBelow: '{{delta}} below the other judges on average, over {{count}} dive(s).',
    meterLearning: 'Judge {{count}} more dive(s) that others also scored to reveal your style.',
    nextHand: 'Next hand in',
    nextHandAria: 'Next selection at {{time}}',
    warmUp: 'The jury is warming up…',
    kindRequest: 'request',
    sortAria: 'Sort my dives',
    sort: { recent: 'Recent', top: 'Top score', votes: 'Most judged' },
    rulesSummary: 'How do community scores work?',
    judgesCount: '{{count}} judge(s)',
    inSelections: 'In selections',
    tabMyDives: 'My dives',
    tabHistory: 'History',
    todayMore: 'Deal {{count}} more',
    requestsTitle: 'Feedback requests',
    requestsPending: '{{count}} dive(s) waiting for your score.',
    requestsCta: 'Judge requests',
    judgedTotal: '{{count}} dive(s) judged in total',
    receiveSection: 'Receive',
    allNotes: '{{count}} scores',
    nextPage: 'Next scores',
    wholeFrame: 'See the whole dive',
    done: 'Done',
    discardTitle: 'Leave without confirming?',
    discardDraft: 'Your unconfirmed score and comment will be lost. Saved scores remain.',
    stay: 'Stay',

    automaticFooter: 'Public dives participate automatically. Manage exclusions in Community scores.',
    tabAutomatic: "Daily selection",
    tabCommunity: "Community scores",
    drawSize: 'Dives per draw',
    drawSizeHint: 'Applies to new daily and extra selections. Saved for your account in this browser; existing selections keep their size.',
    dailyStart: "Start / resume today’s selection",
    automaticNote: "Up to {{count}} public dives from other members. A smaller selection is normal when the community is small. Daily selections reset at midnight UTC.",
    automaticFinished: "Selection finished or no eligible dives available. Saved votes stay separate from requested evaluations.",
    anotherStack: "Request another selection",
    resumeStack: "Resume this selection",
    communityNote: "Public dives can be selected automatically. Exclude any dive below. These experimental scores are provisional, with one automatic vote per member and dive, separate from requested evaluations and official scores. They are not a ranking of divers.",
    communityScore: "{{score}} / 10 · {{count}} reviewers · provisional",
    noPublicDives: "No public dives with video.",
    includeAutomatic: "Include in automatic selections",
    excludeAutomatic: "Exclude from automatic selections",
    previousPage: "Previous",

    label: 'Community evaluation',
    toggleAria: 'Request community evaluation for this dive',
    prompt: 'Comment for the jury (optional)',
    promptPlaceholder: 'What should judges focus on?',
    savePrompt: 'Save comment',
    error: 'Could not update the evaluation request. Try again.',
    mediaPending: 'Video in preparation.',
    quit: 'Quit session',
    previous: 'Previous',
    commentAdd: 'Add a comment',
    backToScore: 'Back to the score keypad',
    composerNeedScore: 'Choose a score first',
    composerFor: 'Your score {{score}} — send to confirm',
    sendAria: 'Confirm {{score}} with this comment',
    promptFold: 'Jury prompt',
    hqUpgrading: 'HD in preparation…',
    commentSavedOnConfirm: 'The comment is stored with your confirmed score.',
    commentEdit: 'Edit comment',
    commentsToggle: 'Comments',
    chooseScore: 'Choose your score',
    ariaScoreOf: '{{score}} out of 10',
    stepUpAria: 'Half point up',
    stepDownAria: 'Half point down',
    you: 'You',
    requestDive: 'Have a dive evaluated',
    requestDiveHint: 'Owners open an evaluation from the Library: toggle “Request community evaluation” on a dive or during an import.',
    mediaChecking: 'Checking the video…',
    keepOpenShort: 'Keep',
    qualityLabelAria: 'Playback quality',
    qualityHQ: 'HD',
    qualityOriginal: 'Original',
    mediaFailed: 'The video could not be prepared. Try again.',
    playerControlsAria: 'Video controls',
    replayAria: 'Replay from the start',
    pauseAria: 'Pause',
    playAria: 'Play',
    soundOnAria: 'Turn the sound on',
    soundOffAria: 'Turn the sound off',
    slowAria: 'Slow motion',
    slowLabel: 'Slow motion',
    mediaRetry: 'Try again',
    title: 'Evaluations',
    lede: 'Judge divers from the club, and collect scores on your own dives.',
    tabsAria: 'Evaluations sections',
    tabJury: 'Jury a session',
    tabMine: 'My evaluations',
    tabJudgements: 'My judgements',
    footerHint: 'Owners open evaluations from the Library or while importing (evaluation toggle).',
    backToLibrary: 'Open the Library',
    signInNeeded: 'Sign in to judge or follow your evaluations.',
    start: 'Start judging',
    startNote: 'Dives arrive in small batches; you can stop anytime and come back.',
    introHint: 'Jury intro',
    introSkip: 'Skip',
    introDemo: 'Demo cards — not real scores',
    counted: '{{count}} dive(s) judged',
    loadError: 'The evaluation queue is unavailable. Try again.',
    retry: 'Retry',
    judgeAria: 'Judging actions',
    scoreAria: 'Score from 0 to 10 by half points',
    noScore: 'No score chosen yet',
    cardAria: 'Score card {{score}}',
    myCardAria: 'Your score card {{score}}',
    scoreHint: '0 to 10 by half points',
    commentAria: 'Optional comment',
    commentPlaceholder: 'Optional comment for the diver…',
    pass: 'Pass',
    confirm: 'Confirm score',
    saving: 'Saving your score…',
    next: 'Next dive',
    keepOpen: 'Keep the results open',
    panelSummary: '{{count}} vote(s) · community average {{mean}}',
    exhausted: 'No more dives to judge right now. New requests will appear here.',
    evaluateAgain: 'Evaluate again',
    mediaMissing: 'Video unavailable. Retry or skip this dive.',
    mediaClosed: 'This evaluation was closed while you watched. Nothing was saved.',
    voteClosed: 'This evaluation just closed — no vote was saved.',
    voteSelf: 'You cannot judge your own dive.',
    voteAlready: 'Your judgement is already confirmed and immutable.',
    voteError: 'The score could not be saved. Your choice is kept; try again.',
    sessionNew: 'A new session started: skipped dives are back, judged ones excluded.',
    unknownOwner: 'Unknown diver',
    ownedCount: '{{count}} request(s)',
    ownedEmpty: 'No evaluation request yet. Open one from your Library or during an import.',
    ownedActive: 'Active',
    ownedClosed: 'Evaluation closed',
    ownedError: 'Your evaluation requests are unavailable. Try again.',
    showPanel: 'Show the jury panel',
    hidePanel: 'Hide the panel',
    panelEmpty: 'No vote yet.',
    panelError: 'The jury panel is unavailable.',
    more: 'Show more',
    judgedCount: '{{count}} judgement(s)',
    judgedEmpty: 'You have not judged any dive yet.',
    judgedError: 'Your judgements are unavailable. Try again.',
    summaryJudged: 'You judged {{count}} dive(s) this session.',
    summarySkipped: 'Passed dives come back in your next session.',
  },
  sessions: {    title: 'Training Sessions',
    sessions: 'Sessions',
    errorLoad: 'Failed to load sessions',
    today: 'Today',
    yesterday: 'Yesterday',
    avgPerSession: 'Average per session',
    scopeAria: 'Session diver scope',
    everyone: 'Everyone',
    myAttempts: 'My attempts',
    inScope: 'In scope',
    none: 'No sessions yet.',
    loadMore: 'Load more sessions',
  },
  sessionDetail: {
    errorLoad: 'Failed to load session details',
    close: 'Close session details',
    bestDive: 'Best Dive',
    diveSequence: 'Dive Sequence',
    videoAvailable: 'Video available',
    videoPlayback: 'Video Playback',
    noVideoSelected: 'No video selected.',
    performanceFlow: 'Performance Flow',
    noScoredAttempts: 'No scored attempts in this session.',
    heightDistribution: 'Height Distribution',
    noHeightData: 'No height data',
    comparison: 'Comparison with Previous Session',
  },
  recall: {
    title: 'Media recall',
    refresh: 'Refresh',
    unknown: 'Unknown',
    unknownLower: 'unknown',
    gib: '{{value}} GiB',
    loadError: 'Unable to load recall operations',
    requestError: 'Unable to request this source',
    catalogStartError: 'Unable to start catalog',
    nodeStatusAria: 'Mac media node status',
    worker: 'Worker',
    notRegistered: 'Not registered',
    leasingEnabled: 'Leasing enabled',
    leasingDisabled: 'Leasing disabled',
    photosAccess: 'Photos access',
    lastQuery: 'Last query {{when}}',
    notRecorded: 'not recorded',
    managedSources: 'Managed sources',
    permanentRecent: 'Permanent {{permanent}} · Recent {{recent}}',
    internalFree: 'Internal free',
    emergencyStop: 'Emergency stop below 18 GiB',
    disabledWhilePhotos: 'Recall remains disabled while Photos access is {{status}}.',
    historicalPaused: 'Historical recall is paused by the internal free-space gate.',
    catalogAria: 'Captioned media catalog',
    catalogTitle: 'Captioned video catalog',
    catalogRunning: 'Catalog running',
    startCatalog: 'Start catalog',
    runState: 'Run state',
    notStarted: 'Not started',
    revision: 'Revision {{revision}}',
    observed: 'Observed',
    checkpoint: 'Checkpoint {{value}}',
    eligible: 'Eligible',
    mappedParseable: 'Mapped and parseable',
    playable: 'Playable',
    posterProxyReady: 'Poster + proxy ready',
    excluded: 'Excluded',
    paused: 'Paused: {{reason}}',
    populationTitle: 'Rolling population plan',
    planPopulation: 'Plan population',
    alreadyReady: 'Already ready',
    rollingPeak: 'Rolling peak',
    projectedFree: 'Projected free',
    execution: 'Execution',
    allowed: 'Allowed',
    batchTitle: 'Zero-ready Typical plan',
    planBatch: 'Plan bounded batch',
    items: 'Items',
    peak: 'Peak',
    gate: 'Gate',
    manifestTitle: 'Bounded recall manifest',
    itemsShown: '{{count}} items shown',
    typical: 'Typical',
    attempt: 'Attempt',
    unknownDiver: 'Unknown diver',
    poster: 'Poster',
    video: 'Video',
    requesting: 'Requesting…',
    recallOne: 'Recall one',
    colDive: 'Dive',
    colReason: 'Reason',
    colSource: 'Source',
    colMedia: 'Media',
    colDate: 'Date',
    colAction: 'Action',
    noItems: 'No recall items.',
  },
  ddWorkspace: {
    loadingAria: 'Loading degree of difficulty',
    loadError: 'Difficulty data could not be loaded.',
    title: 'Degree of difficulty',
    view: 'View',
    scopeAria: 'Difficulty scope',
    myDives: 'My dives',
    allDivers: 'All divers',
    definedAttempts: 'Defined attempts',
    definedOf: '{{defined}} of {{total}}',
    combinations: '{{count}} dive and board combinations',
    medianDD: 'Median DD',
    highestDD: 'Highest DD',
    notDefined: 'Not defined',
    monthlyRange: 'Monthly difficulty range',
    monthsCount: '{{count}} months',
    heightContext: 'Board-height context',
    attemptsCount: '{{count}} attempts',
    medianValue: 'Median {{value}}',
    maxValue: 'Max {{value}}',
    inventory: 'Recorded DD inventory',
    legacyMismatch: '{{count}} stored legacy DD values differ from the formula result.',
    excludedFromOfficial: 'Excluded from official DD: {{list}}',
  },
  avoidedDives: {
    loadError: 'Failed to load avoided dives analysis',
    none: 'No avoided dives.',
    title: 'Avoided Dives',
    notAttempted_one: '{{count}} dive not attempted recently.',
    notAttempted_other: '{{count}} dives not attempted recently.',
    avoidanceScore: 'Avoidance Score',
    daysAway: 'Days Away',
    difficulty: 'Difficulty',
    daysWithoutAttempt: 'Days Without Attempt',
    daysCount: '{{count}} days',
    attempts: 'Attempts',
    lastPerformance: 'Last performance:',
    performance: {
      F: 'Failed',
      B: 'Bad',
      M: 'Medium',
      G: 'Good',
      P: 'Perfect',
    },
  },
  mentalBlock: {
    none: 'No mental blocks detected',
    ddPlateau: 'Difficulty Plateau',
    repeatedFailure: 'Repeated Failure Pattern',
    avoidedDive: 'Avoided Dive',
    summary: '{{months}} • Affecting {{dives}}',
    months_one: '{{count}} month',
    months_other: '{{count}} months',
    dives_one: '{{count}} dive',
    dives_other: '{{count}} dives',
    affectedDives: 'Affected Dives:',
    recoveryPlan: 'Recovery Plan:',
    checkProgress: 'Check Progress',
    dismiss: 'Dismiss',
  },
  clipNav: {
    previous: 'Previous',
    next: 'Next',
    speed: 'Speed',
    playbackSpeed: 'Playback speed',
    positionAria: 'Position {{index}} of {{total}} in the current list',
  },
  sessionDirection: {
    label: 'Directions:',
    short: {
      1: 'Forw.',
      2: 'Back',
      3: 'Reve.',
      4: 'Inwa.',
      5: 'Twis.',
    },
  },
  videoPlayer: {
    loadingVideo: 'Loading video',
    preparingTitle: 'Video on its way',
    preparingHint: 'It is coming from your Mac, usually in under a minute.',
    preparingSlow: 'This is taking longer than usual.',
    preparingPaused: 'Preparation paused',
    connectionLost: 'Connection interrupted',
    tryAgainHint: 'Try again in a moment.',
    retry: 'Try again',
    steps: { retrieve: 'Fetching', convert: 'Converting', send: 'Sending' },
    unreachableTitle: 'Can’t connect',
    unreachableHint: 'Check your connection and try again.',
    unavailableTitle: 'Video unavailable',
    unavailableHint: 'This video can’t be played right now.',
    unavailablePrepareHint: 'Preparing it failed. You can start it again.',
    prepareAgain: 'Prepare again',
    signInToPlay: 'Sign in to play',
    sessionExpired: 'Session expired.',
    signIn: 'Sign in',
    videoAria: '{{code}} {{height}} dive video',
    statusCheckFailed: 'Media status check failed',
    retryStatusCheck: 'Retry status check',
    mediaReady: 'Media ready.',
    checking: 'Checking…',
    lastKnownState: 'Last known state: {{state}}',
    statusChecksStopped: '{{state}} — status checks stopped',
    connectionInterrupted: '{{state}} — connection interrupted',
    stillPreparing: 'Still preparing',
    preparingMedia: 'Preparing media: {{state}}',
    checkStatusAgain: 'Check status again',
    checkStatusNow: 'Check status now',
    noVideo: 'No video available',
    prepareFailed: 'Media preparation failed',
    requestFailed: 'Could not request media preparation',
    requesting: 'Requesting…',
    retryPrepare: 'Retry prepare',
    usePreview: 'Play a lower-quality preview',
    changeQuality: 'Change video quality',
    qualityCycle: '{{current}} — tap to change quality',
    originalQuality: 'Original',
    previewLabel: 'Preview',
    lightLabel: 'Light',
    hqLabel: 'HQ',
    previewQuality: 'Lower-quality preview',
    loadFailed: 'Video failed to load',
    tryAgain: 'Try again',
    notCached: 'Clip not cached',
    prepareClip: 'Prepare clip',
    noPlayable: 'No playable media for this clip right now.',
    posterUnavailable: 'No preview image available',
    states: {
      queued: 'queued',
      waiting_for_mac: 'waiting for mac',
      claimed: 'claimed',
      materializing: 'retrieving source video',
      exporting: 'exporting',
      transcoding: 'transcoding',
      uploading: 'uploading',
      verifying: 'verifying',
    },
  },
  nav: {
    sessionVideos: 'Session videos',
    calendar: 'Calendar',
    messages: "Messages",
    messagesUnreadAria: "Messages, {{count}} unread",
    community: "Community",    dives: 'Library',
    evaluations: 'Evaluations',
    overview: 'Overview',
    journey: 'Journey',
    analytics: 'Analytics',
    representatives: 'Representatives',
    sessions: 'Sessions',
    notifications: 'Notifications',
    mediaRecall: 'Media recall',
    system: 'System',
    account: 'Account',
    operations: 'Operations',
    operationsAria: 'Operations navigation',
    playerCanary: 'Player canary',
    review: 'Review',
    workspace: 'Workspace',
    primaryNavigationAria: 'Primary navigation',
    secondaryNavigationAria: 'Secondary navigation',
    myProfile: 'My profile',
    accountNavigationAria: 'Account navigation',
    workspaceSidebarAria: 'Workspace sidebar',
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
    skipToContent: 'Skip to main content',
  },
  roles: {
    diver: 'diver',
    administrator: 'administrator',
    Diver: 'Diver',
    Administrator: 'Administrator',
  },
  breadcrumbs: { analysis: 'analysis' },
  notFound: {
    title: 'Page not found',
    body: 'Nothing lives at this address.',
    backToLibrary: 'Back to Library',
  },
  login: {
    title: 'Sign in',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    signingIn: 'Signing in…',
    staySignedIn: 'Stay signed in for 30 days',
    newHere: "New here?",
    createDiverAccount: 'Create a diver account',
    invalidCredentials: 'Invalid email or password',
    networkError: 'Network error — please try again',
  },
  register: {
    club: 'Club',
    clubNone: 'No club (independent diver)',
    clubHelp: 'Self-declared: choosing a club makes you a member immediately. It is not a verified licence and gives no training group or coach role; a coach assigns groups.',
    errorClub: 'This club is no longer available. Choose another option.',
    subtitle: 'Diver account — view public dives and manage your own.',
    closed:
      'Registration is currently closed. Ask an administrator for a current invite code.',
    displayName: 'Display name',
    confirmPassword: 'Confirm password',
    inviteCode: 'Invite code',
    invitePlaceholder: 'Shared by an administrator',
    creating: 'Creating account…',
    submit: 'Create diver account',
    alreadyHaveAccount: 'Already have an account?',
    errorMismatch: 'Passwords do not match',
    errorTooShort: 'Password must be at least 10 characters',
    errorInvite: 'Invalid invite code',
    errorExists: 'An account with this email already exists',
    errorTooMany: 'Too many attempts — please try again later',
    errorFailed: 'Registration failed — please check your details',
  },
  invite: {
    errorClub: 'This club is no longer available. Choose another option.',
    subtitle: 'You have been invited to claim the dives of',
    unknownDiver: 'your diver',
    expiresOn: 'This link expires on {{date}}',
    invalid: 'This invitation link is invalid or has already been used.',
    invalidHint: 'Ask whoever invited you to send a new link.',
    backToLogin: 'Back to sign in',
    creating: 'Creating your account…',
    submit: 'Create my account',
    errorMismatch: 'Passwords do not match',
    errorTooShort: 'Password must be at least 10 characters',
    errorLink: 'This invitation link is invalid or has already been used.',
    errorClaimed: 'These dives already belong to another account. Ask for a new link, or sign in instead.',
    errorExists: 'An account with this email already exists — sign in instead',
    errorTooMany: 'Too many attempts — please try again later',
    errorFailed: 'Account creation failed — please check your details',
  },
  fedUI: {
    title: 'Fediverse cross-post',
    subtitle: 'Poster + caption. No video, no video link.',
    switchAria: 'Allow fediverse cross-post for this dive',
    onlyPublic: 'Only public dives can be federated',
    checking: 'Checking eligibility…',
    neverPublishesNote: 'Turning this on never publishes past dives — each dive needs its own opt-in.',
    copied: 'Cross-post text copied',
    copyText: 'Copy cross-post text',
    copiedNote: 'Poster + caption copied. Do not paste a Share link — it plays the original video. Attach the poster image yourself when posting.',
    errorStatus: 'Fediverse status unavailable.',
    errorCrossPost: 'Cross-post unavailable — check the opt-ins above.',
    reason: {
      globalOff: 'Turn on fediverse sharing in Profile & settings first.',
      divePrivate: 'Only public dives can be federated. Private dives stay local.',
      optInOff: 'Opt in this dive to allow a manual cross-post.',
      eligible: 'Eligible for a manual cross-post (poster + caption, no video link).',
    },
  },
  account: {
    title: 'Profile & settings',
    signedInAs: 'Signed in as',
    diverHint:
      'You can view public dives shared by divers and manage your own dives. Private dives are visible only to their owners. Forgotten passwords need an administrator reset.',
    adminHintLink: 'Manage users in Administration',
    adminHint:
      ' — create diver and administrator accounts, reset passwords, and manage diver bindings.',
    managedByAdmin:
      'Profile details are managed by your administrator. To change your display name, ask an admin.',
    diverScore: {
      title: 'Diver score',
      subtitle: 'One 0–100 composite for your account: dive quality, judging, and community presence.',
      provisional: 'Provisional',
      performance: 'Performance {{mean}} / 10 across {{count}} votes',
      judging: '{{count}} dives evaluated',
      community: '{{count}} community interactions',
    },
    notifications: 'Notifications',
    notificationsStored: 'In-app alerts sync to your account; email and digest stay on this device for now.',
    notifInApp: 'In-app alerts',
    notifInAppDesc: 'Synced to your account from the server.',
    notifOnDevice: 'On this device',
    notifLoadError: 'Settings could not be loaded. Showing last known values.',
    notifSaveError: 'Could not save. Try again.',
    notifSaving: 'Saving…',
    notifNewVideo: 'New video alerts',
    notifNewVideoDesc: 'A new clip lands in your library',
    notifVideoReady: 'Video ready alerts',
    notifVideoReadyDesc: 'Processing finished and playback is available',
    notifNewComment: 'New comment alerts',
    notifNewCommentDesc: 'Someone comments on a shared dive',
    notifCommentReply: 'Comment reply alerts',
    notifCommentReplyDesc: 'Someone replies to your comment',
    notifSyncError: 'Sync issue alerts',
    notifSyncErrorDesc: 'A sync needs your attention',
    notifGuestActivity: 'Guest activity alerts',
    notifGuestActivityDesc: 'Opt-in: likes and comments from guests',
    notifCalendarHeading: 'Calendar',
    notifCalendarDesc: 'Lock-screen previews of club and private events stay generic.',
    notifCalendarInvitation: 'Invitations',
    notifCalendarInvitationDesc: 'Someone invites you to an event',
    notifCalendarChange: 'Changes and cancellations',
    notifCalendarChangeDesc: 'Time, place or cancellation of events you are interested in, invited to, or your training group’s sessions',
    notifCalendarReminder: 'Reminder the day before',
    notifCalendarReminderDesc: 'For sessions you marked “Interested”',
    notifCalendarGroupReminder: 'Reminder for every group session',
    notifCalendarGroupReminderDesc: 'Opt-in: the day before each session of your assigned group, unless you reported an absence',
    notifCalendarFollow: 'Followed events and calendars',
    notifCalendarFollowDesc: 'New events and changes in what you follow (never bulk club imports)',
    notifEmail: 'Email notifications',
    notifEmailDesc: 'On this device only for now — email delivery ships in a later update',
    notifMilestones: 'Milestone alerts',
    notifMilestonesDesc: 'Personal bests and streaks',
    notifDigest: 'Weekly digest',
    notifDigestDesc: 'On this device only for now — the server digest ships in a later update',
    pushTitle: 'Push notifications on this device',
    pushDesc: 'Get alerts even when the app is closed. Only the alert types you keep on above can send a push — never spam.',
    pushEnable: 'Enable push on this device',
    pushDisable: 'Disable push on this device',
    pushWorking: 'Working…',
    pushOn: 'On — this device receives push alerts.',
    pushOff: 'Off — this device gets no push alerts.',
    pushUnsupportedBrowser: 'Push is not supported in this browser.',
    pushUnsupportedServer: 'Push is not set up on the server yet.',
    pushBlocked: 'Notifications are blocked for this site. Allow them in your browser settings, then try again.',
    pushIosHint: 'iPhone or iPad: add the app to your Home Screen first (Share → Add to Home Screen). Requires iOS 16.4 or later.',
    pushError: 'Push settings could not be changed. Try again.',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    newPasswordHint: '(10+ characters)',
    confirmNewPassword: 'Confirm new password',
    changing: 'Changing…',
    sessions: 'Sessions',
    sessionsHint: 'This session ends when you sign out.',
    security: 'Security',
    signOut: 'Sign out',
    errorMismatch: 'New passwords do not match',
    errorTooShort: 'New password must be at least 10 characters',
    errorWrongPassword: 'Current password is incorrect',
    errorChangeFailed: 'Password change failed — please try again',
    errorNetwork: 'Network error — please try again',
    passwordChanged: 'Password changed. Other sessions were signed out.',
    fediverse: {
      section: 'Fediverse',
      heading: 'Fediverse sharing',
      description: 'Manual cross-posts to open social apps (Mastodon, Pixelfed). Off by default.',
      noteAria: 'Turning on does not publish existing dives',
      noteStrong: 'Turning this on does not publish anything yet.',
      noteLocal: 'Your existing public dives stay local.',
      notePublicWord: 'public',
      noteOptIn: 'Each dive needs its own opt-in afterwards — private dives are never eligible.',
      noteContentWord: 'poster + caption only',
      noteContent: ' No video and no video link (a Share link plays the original video, so never paste one).',
      noteAttach: 'Attach the poster image yourself when posting.',
      statusOn: 'On — you can opt in public dives one by one.',
      statusOff: 'Off — no dive can be cross-posted.',
      saving: 'Saving…',
      errorLoad: 'Fediverse settings unavailable. Try again.',
      errorSave: 'Could not save. Try again.',
      step1: 'Step 1: turn this on. Nothing is published.',
      step2: 'Step 2: open a public dive → turn on “Fediverse cross-post” for that dive.',
      step3: 'Step 3: use “Copy cross-post text” and attach the poster image yourself. Never paste a Share link.',
      behaviorNote: 'Works the same on phones, tablets, and desktop — this warning is always shown, with no hover-only steps.',
    },
    sectionsAria: 'Profile sections',
    sections: { profile: 'Profile', activity: 'Activity', notifications: 'Notifications', security: 'Security' },
    profileAria: 'Profile',
    notificationsAria: 'Notifications',
    securityAria: 'Security',
    activity: {
      title: 'Activity history',
      subtitle: 'Interactions on your dives, and the interactions you made on others. Click a name for the member profile, or a clip to open it.',
      tabsAria: 'Activity filter',
      tabs: { all: 'All', received: 'Received', sent: 'Sent' },
      empty: 'No activity yet — likes and comments on your dives will show up here.',
      emptySent: 'Your likes and comments on visible dives will show up here.',
      loading: 'Loading activity…',
      loadOlder: 'Load older activity',
      errorLoad: 'Activity history unavailable. Try again.',
      guestNote: 'Guests from share links are shown with their display name only.',
      trail: {
        sent: {
          dive_like: 'You liked the clip',
          comment: 'You commented on the clip',
          comment_like: 'You liked a comment on the clip',
        },
        received: {
          dive_like: 'liked your clip',
          comment: 'commented on your clip',
          comment_like: 'liked your comment on',
        },
      },
    },
  },
  avatar: { title: 'Profile picture', description: 'Your photo and HUD follow you across profiles, comments and activity.', upload: 'Add photo', replace: 'Replace photo', hud: 'Choose your HUD', orbit: 'Orbit', reticle: 'Reticle', depth: 'Depth', saved: 'Saved — your identity is up to date.' },
  achievements: {
    title: "Achievements",
    loading: "Loading achievements…",
    errorLoad: "Could not load achievements.",
    emptyOther: "No achievements unlocked yet.",
    summary: "{{unlocked}} of {{total}} unlocked",
    locked: "locked",
    unlockedOn: "Unlocked {{date}}",
    unlocked: "Achievement unlocked",
    unlockedMany: "{{count}} achievements unlocked",
    view: "See my achievements",
    dismiss: "Dismiss",
    categories: {
      profile: "Profile",
      community: "Community",
      jury: "Jury",
      dives: "Dives",
    },
    items: {
      profile_photo: { title: "Picture perfect", description: "Add a profile photo." },
      profile_handle: { title: "Name tag", description: "Choose your @handle." },
      profile_bio: { title: "Say something", description: "Write your bio." },
      profile_complete: { title: "Fully set up", description: "Complete your profile: photo, handle and bio." },
      first_like: { title: "First like", description: "Like a dive." },
      likes_25: { title: "Fan club", description: "Like 25 dives." },
      first_comment: { title: "First comment", description: "Comment on a dive." },
      first_reply: { title: "Let's talk", description: "Reply to a comment." },
      comments_10: { title: "Regular voice", description: "Write 10 comments or replies." },
      first_mention: { title: "Tagged in", description: "Mention a member with @ in a comment or message." },
      first_message: { title: "Slide into DMs", description: "Send a private message." },
      first_like_received: { title: "Someone noticed", description: "Receive a like on one of your dives." },
      first_comment_received: { title: "Feedback received", description: "Receive a comment on one of your dives." },
      first_rating: { title: "On the jury", description: "Score a dive as a community judge." },
      ratings_10: { title: "Seasoned judge", description: "Score 10 dives as a judge." },
      first_evaluation_request: { title: "Judge me", description: "Ask the community to judge one of your dives." },
      first_share: { title: "Share the splash", description: "Share a dive with a link." },
    },
  },
  community: {
    results: 'Results',
    recentlyActive: 'Recently active',
    divers: 'Divers',
    otherMembers: 'Other members',
    you: 'You',
    codes: 'Codes',
    board: {
      title: 'Rankings',
      week: 'Week',
      month: 'Month',
      periodAria: 'Ranking period',
      categoryAria: 'Ranking category',
      podium: 'Podium',
      active: 'Most active',
      streak: 'Consistency',
      progress: 'Improving',
      explorer: 'Explorers',
      unit: {
        active_one: '{{count}} dive', active_other: '{{count}} dives',
        streak_one: '{{count}} week', streak_other: '{{count}} weeks',
        progress: '{{value}} median',
        explorer_one: '{{count}} code', explorer_other: '{{count}} codes',
      },
      empty: {
        active: 'Nobody dived in this period.',
        streak: 'No weekly streak running.',
        progress: 'Not enough graded attempts to compare.',
        explorer: 'No codes in this period.',
      },
    },
    sharedDives_one: '{{count}} shared dive',
    sharedDives_other: '{{count}} shared dives',
    noSharedDives: 'No shared dives yet',
    latestDive: 'Latest dive',

    "title": "Community",
    "myProfile": "My profile",
    "search": "Search by name or @handle",
    "error": "Could not load members.",
    "empty": "No members match your search.",
    "more": "Load more members",
    remaining_one: 'Show the remaining {{count}} member',
    remaining_other: 'Show the remaining {{count}} members',
    "back": "Community",
    "edit": "Edit profile",
    "name": "Display name",
    "handle": "Handle",
    "handleHint": "Optional. 3–30 letters, numbers or underscores. Handles are unique.",
    "bio": "Bio",
    "avatar": "Profile photo",
    "avatarHint": "JPEG, PNG or WebP · up to 2 MB and 16 megapixels. Photo changes save immediately.",
    "removeAvatar": "Remove photo",
    "avatarInvalid": "Use a valid JPEG, PNG or WebP up to 2 MB and 16 megapixels.",
    "saveError": "Could not save your profile. Please try again.",
    "handleTaken": "This handle is already taken. Choose another one.",
    "dives": "Dives",
    "activity": "Activity",
    "achievements": "Achievements",
    "profileSections": "Profile sections",
    "latest": "Latest dives first · only dives you can access appear here.",
    "openDive": "Watch {{code}} · {{height}}",
    "privateDive": "Private dive",
    "divesError": "Could not load these dives.",
    "noDives": "No dives to show yet.",
    "moreDives": "Load more dives"
},
  profile: {
    ago: { hours: 'h', days: 'd', months_one: 'mo', months_other: 'mo', years_one: 'yr', years_other: 'yr' },
    stats: 'Stats',
    kpi: { dives: 'Dives', codes: 'Codes', sessions: 'Sessions', last: 'Last' },
    weeksAria: 'Dives per week over {{count}} weeks',
    thisWeek: 'this week',
    breadcrumb: 'Member profile',
    memberSince: 'Member since {{date}}',
    diverOf: 'Diver:',
    emptyActivity: 'No activity to show yet.',
    errorLoad: 'Member profile unavailable.',
    notFoundTitle: 'Member not found',
    today: 'Today',
    yesterday: 'Yesterday',
    trail: {
      dive_like: 'liked the clip',
      comment: 'commented on the clip',
      comment_like: 'liked a comment on the clip',
    },
  },
  settings: {
    language: 'Language',
    languageDesc:
      'Applies immediately. Dive names follow World Aquatics diving terminology.',
    languageSystemOption: 'Same as device',
    languageChanged: 'Language updated.',
  },
  dashboard: {
    title: 'Training Dashboard',
    errorLoad: 'Failed to load dashboard data',
    lastUpdated: 'Last updated',
    syncProgress: 'Sync',
    totalDives: 'Total Dives',
    successRate: 'Success Rate',
    successRateSubtitle: 'Medium or better',
    avgPerformance: 'Avg Performance',
    avgPerformanceSubtitle: 'Calibrated /10',
    avgDD: 'Avg DD',
    maxDD: 'Max DD',
    trainingSessions: 'Training Sessions',
    personalDiveValue: 'Personal Dive Value',
    highestRecent: 'highest recent',
    latestEligibleAttempts: 'latest eligible attempts',
    personalValueHistoryAria: 'Recent Personal Dive Value history',
    monthlyProgression: 'Monthly Progression',
    performanceByHeight: 'Performance by Height',
    byDirection: 'By Direction',
    successRateTrend: 'Success Rate Trend',
    trainingFocus: 'Training Focus',
    dives: 'Dives',
    recentSessions: 'Recent Training Sessions',
    viewAll: 'View all →',
    dirFallback: 'Dir',
    date: 'Date',
    personal: 'Personal',
    averagePerformance: 'Average performance',
  },
  jargon: {
    springboard: 'springboard',
    platform: 'platform',
    unknownPosition: 'Unknown position',
    positions: { A: 'Straight', B: 'Pike', C: 'Tuck', D: 'Free' },
    phaseN: 'Phase {{n}}',
    heights: {
      '1m': '1 m',
      '3m': '3 m',
      '5m': '5 m',
      '7m': '7.5 m',
      '7.5m': '7.5 m',
      '10m': '10 m',
    },
    phase1: 'Hurdle + flight',
    phase2: 'Opening + entry',
  },
  groups: {
    1: 'Forward',
    2: 'Back',
    3: 'Reverse',
    4: 'Inward',
    5: 'Twist',
  },
  performance: {
    P: 'Perfect',
    G: 'Good',
    M: 'Medium',
    B: 'Below target',
    F: 'Failed',
  },
  attemptDetail: {
    score: 'Score',
    diver: 'Diver',
    officialDD: 'Official DD',
    notDefined: 'Not defined',
    phases: 'Phases',
    singleRating: 'Single rating',
    height: 'Height',
    platform: 'Platform',
    media: 'Media',
    rawCaption: 'Raw caption',
    noCaption: 'No caption preserved.',
    notPreserved: 'Not preserved',
    notes: 'Notes',
  },
  dives: {
    openCodeAttempt: 'Open {{code}} attempt',
    openLatestAttempt: 'Open latest {{code}} attempt',
    avg: 'avg',
    best: 'best',
    last: 'last',
    attemptsCount: '{{count}} attempts',
    recentAttemptsFor: '{{code}} recent attempts',
    openAttemptFrom: 'Open {{code}} attempt from {{date}}',
    openAttemptAria: 'Open {{code}} attempt {{date}}',
    score: 'Score',
    errorSaved: 'Showing saved dives. The library could not be refreshed.',
    errorLoad: 'The dive library could not be loaded.',
    attempts: 'attempts',
    codes: 'codes',
    refresh: 'Refresh library',
    viewClips: 'Clips',
    viewByCode: 'By code',
    filtersAria: 'Dive library filters',
    myDives: 'My dives',
    community: 'Community',
    searchPlaceholder: 'Search dives…',
    searchAria: 'Search dives',
    filters: 'Filters',
    gridView: 'Grid view',
    listView: 'List view',
    codeView: 'Code view',
    heightFilter: 'Height',
    allHeights: 'All heights',
    heightsPartial: 'More heights exist (the list is partial).',
    heightsPartialAria: 'The height list is partial: more heights exist beyond this bounded view. Open the Library filters to load them as you page.',
    contextFilter: 'Context',
    contextAll: 'Individual + SYNC',
    contextIndividual: 'Individual only',
    contextSync: 'SYNC only',
    ratingFilter: 'Rating',
    allRatings: 'All ratings',
    scoreChipTitle: '{{score}} on the 0–10 scale — poster letter {{letter}} (P · Perfect … F · Failed).',
    modifierHelp: 'What do DB, SD, HU, SYNC and DD mean?',
    modifierNames: { DB: 'Double bounce', SD: 'Standing dive', HU: 'Hands up' },
    modifierDescriptions: {
      DB: 'Two bounces on the springboard instead of a full hurdle.',
      SD: 'Standing take-off without a hurdle for forward or reverse dives.',
      HU: 'Hands held up throughout the hurdle.',
    },
    helpSyncName: 'Synchronized',
    helpSyncDescription: 'The attempt belongs to a synchronized pair — two divers diving aligned, often from a shared session.',
    helpDdName: 'Official difficulty (World Aquatics table)',
    helpDdDescription: 'The DD weighs code, height and position. Scores sit on the separate 0–10 scale.',
    ratingsItem: 'Rating letters',
    ratingsScale: 'P · Perfect 10 — G · Good 7 — M · Medium 4.5 — B · Below target 2 — F · Failed 0.',
    modifiers: 'Modifiers',
    resetFilters: 'Reset filters',
    noMatches: 'No attempts match this search.',
    clearFilters: 'Clear filters',
    loadMore: 'Load more dives',
    showMoreAttempts: 'Show 48 more attempts',
    openingVideo: 'Opening video…',
  },
  codeAnalysis: {
    progressTab: 'Progress',
    sortAria: 'Sort codes',
    sort: { recent: 'Recent', volume: 'Most practised', progress: 'Improving' },
    openCode: 'Analyse {{code}}',
    median: 'median',
    average: 'average',
    success: 'success',
    summary: 'Code summary',
    attemptsSessions: '{{attempts}} attempts · {{sessions}} sessions',
    attemptsShort: '{{count}} att.',
    stable: 'Stable',
    trendTooOld: 'Not recent enough',
    sinceLastAttempt: 'since the last attempt',
    rising: 'Improving',
    falling: 'Dropping',
    sixWeeks: 'last 6 wks',
    periodAria: 'Period',
    period: { '1m': '1 month', '3m': '3 months', '1y': '1 year', all: 'All' },
    sample: '{{attempts}} attempts · {{sessions}} sessions · {{from}} → {{to}}',
    sampleCapped: '{{attempts}} attempts shown (of {{total}}) · {{sessions}} sessions · {{from}} → {{to}}',
    cappedAria: 'History capped at the newest {{total}} attempts for this code.',
    cappedPlain: 'History capped to the newest attempts window for this view.',
    successTitle: 'Share of graded attempts scoring G (7/10) or better.',
    rankTitle: 'Ranked by score among the {{total}} attempts of this code; ties share a rank.',
    thisDive: 'This dive',
    rankFirst: '#1 of {{total}}',
    rankOf: '#{{rank}} of {{total}}',
    vsMedian: 'vs your median',
    vsPreviousDive: 'vs previous ({{score}})',
    record: 'personal best',
    bestSince: 'best in {{count}} wks',
    scores: 'Scores',
    chartModeAria: 'Chart view',
    modeAttempts: 'Attempts',
    modeSessions: 'Sessions',
    chartLabel: 'Scores for {{count}} points of {{code}}',
    chartKeys: 'Tap or drag to browse; left and right arrow keys on a keyboard.',
    legendMedian: 'rolling median',
    legendRange: 'usual range',
    peak: 'peak',
    valley: 'dip',
    peakExplain: 'Peak · median {{median}} over {{count}} attempts ({{from}} → {{to}}). Here is the best of that stretch.',
    valleyExplain: 'Dip · median {{median}} over {{count}} attempts ({{from}} → {{to}}). Here is the weakest of that stretch.',
    watch: 'Watch',
    context: 'By context',
    phase: 'Phase {{n}}',
    phase1Costs: 'Phase 1 is costing points.',
    phase2Costs: 'Phase 2 is costing points.',
    phasesEven: 'Both phases are even.',
    sessions: 'Sessions',
    allSessions: 'Show all {{count}} sessions',
    fewerSessions: 'Show fewer',
    representatives: 'Representatives',
    rep: { best: 'Best', latest: 'Latest', typical: 'Typical', review: 'To review' },
    related: 'Related codes',
    community: {
      title: 'Community',
      legend: 'median · middle half',
      you: 'You',
      others: 'Others',
      ranking: 'Ranking',
      compareWith: 'Compare with {{name}}, median {{median}}',
      vsYou: 'vs {{score}} for you',
      chartLabel: 'Rolling median over the last 40 attempts: you, {{name}} and the other divers',
      best: 'Best',
      fullAnalysis: 'Full analysis',
      profile: 'Profile',
      hidden_one: '+{{count}} diver with fewer than 3 graded attempts',
      hidden_other: '+{{count}} divers with fewer than 3 graded attempts',
      backToMine: 'My analysis',
    },
    relation: { position: 'other position', twists: 'more or fewer twists', noTwist: 'without twist', group: 'same group' },
    details: 'Detailed analysis',
    straightJump: 'Straight jumps are not scored: only the videos are available.',
    emptyPeriod: 'No {{code}} in this period.',
    showAll: 'Show all',
    loadError: 'This code could not be loaded.',
    kpi: { attempts30: 'Attempts · 30 d', sessions30: 'Sessions · 30 d', codes30: 'Codes practised · 30 d', median30: 'Median · 30 d' },
    vsPrevious30: 'vs the 30 d before',
    nothingClear: 'Nothing clear',
    resume: 'To pick up again',
    resumeHint: 'not done for {{days}} d',
    allCurrent: 'All up to date',
    heatTitle: 'Codes × months',
    heatHint: 'median',
    heatCell: '{{code}} · {{month}} · {{count}} att. · median {{median}}',
    volume: 'Volume per week',
    weeks: '{{count}} weeks',
    weekOf: 'week of {{date}} · {{count}} att.',
    emptyWeek: 'No dives that week',
  },
  analysis: {
    sample: {
      noData: 'No data',
      insufficient: 'Insufficient',
      exploratory: 'Exploratory',
      moderateSample: 'Moderate sample',
      strongerSample: 'Stronger descriptive sample',
    },
    trendDirection: {
      improving: 'improving',
      declining: 'declining',
      stable: 'stable',
      insufficient_data: 'insufficient data',
      plateauing: 'plateauing',
      struggling: 'struggling',
    },
    dimension: {
      modifier: 'modifier',
      height: 'height',
      position: 'position',
      sync: 'SYNC',
      diver: 'diver',
      training_period: 'training period',
    },
    previousUnavailable: 'Previous unavailable',
    noChange: 'No change',
    vsPrevious: 'vs previous',
    pointLabel: '{{date}}, score {{score}}, {{height}}, {{position}}, {{modifiers}}, {{context}}, media {{media}}',
    sessionLabel: '{{date}}, session {{session}}, range {{min}} to {{max}}, median {{median}}, {{attempts}} attempts',
    periodLabel: '{{start}} to {{end}}, range {{min}} to {{max}}, median {{median}}, {{attempts}} attempts',
    combined: 'Combined',
    noModifier: 'No modifier',
    individual: 'Individual',
    noModifiers: 'No modifiers',
    unassigned: 'Unassigned',
    none: 'None',
    any: 'Any',
    first: 'first',
    latest: 'latest',
    in: 'in',
    emerging: 'emerging',
    attempts: 'attempts',
    attemptsToReview: 'Attempts to review',
    sessionsLabel: 'sessions',
    cachedClips: 'cached clips',
    thisAttempt: 'This attempt',
    attemptNotInScope: 'The linked clip is not part of this analysis view. Showing all {{code}} attempts.',
    diveCode: 'Dive code',
    analyze: 'Analyze',
    execution: 'execution',
    score: 'score',
    position: 'Position',
    positions: 'Positions',
    heights: 'Heights',
    context: 'Context',
    modifiers: 'Modifiers',
    modifier: 'Modifier',
    modifierMatch: 'Modifier match',
    modifierAny: 'Any selected',
    modifierAll: 'All selected',
    diver: 'Diver',
    diverScope: 'Diver scope',
    allDivers: 'All divers',
    selectedDivers: 'Selected divers',
    selectedDiversCount: '{{count}} selected divers',
    primary: 'primary',
    trainingPeriod: 'Training period',
    trainingPeriods: 'Training periods',
    allPeriods: 'All periods',
    dateFrom: 'Date from',
    dateTo: 'Date to',
    dateRange: '{{from}} to {{to}}',
    aggregateScope: '{{code}} · {{scope}} · {{attempts}} attempts · current filters',
    filters: 'Filters',
    view: 'View',
    chartView: 'Chart view',
    sessionSummary: 'Session summary',
    performanceRange: 'Performance range',
    allAttempts: 'All attempts',
    splitBy: 'Split by',
    splitByHeight: 'Split by height',
    splitByPosition: 'Split by position',
    byAttempts: 'By attempts',
    bySessionMedians: 'By session medians',
    weighting: 'Weighting',
    weightingHint: 'Attempt weighting gives every repetition equal influence. Session weighting gives each session median equal influence.',
    resetAll: 'Reset all',
    copyLink: 'Copy link',
    last3Months: 'Last 3 months',
    last6Months: 'Last 6 months',
    progression: 'Progression',
    rangeByPeriod: 'Performance range by period',
    scoreProgression: 'Attempt score progression over time',
    scoresRange: 'Scores range from {{min}} to {{max}}, median {{median}}, with {{spread}} spread across {{attempts}} attempts.',
    spreadWide: 'wide',
    spreadModerate: 'moderate',
    chartLegend: 'Chart legend',
    legendMax: 'Max',
    legendMedian: 'Median',
    legendMin: 'Min',
    legendRangeHint: 'One vertical range per adaptive time period · select a median to open an exact attempt',
    legendIndividual: 'Individual attempt',
    legendSync: 'SYNC attempt',
    legendRolling: 'Rolling 5-attempt median',
    legendReady: 'Video ready outline',
    noScoredAttempts: 'No scored attempts are available for this series.',
    scoreDistribution: 'Score distribution',
    contextBreakdown: 'Context breakdown',
    equivalentOnly: 'Equivalent contexts only',
    mixedContext: 'Mixed comparison context',
    equivalentContext: 'Equivalent context',
    sampleHeading: 'Sample',
    allPositions: 'All positions',
    reviewBest: 'Review best attempt ({{score}})',
    ddCoverage: 'DD coverage',
    threshold: 'threshold',
    coverage: 'coverage',
    difficulty: 'Difficulty',
    comparisonHistory: 'Comparison & history',
    periodComparison: 'Period comparison',
    pickDateRange: 'Pick a date range to compare with the previous period.',
    measure: 'Measure',
    selectedPeriod: 'Selected period',
    previousPeriod: 'Previous equal period',
    change: 'Change',
    selected: 'Selected',
    previous: 'Previous',
    median: 'Median',
    average: 'Average',
    averageExecution: 'Average execution',
    medianExecution: 'Median execution',
    avg: 'avg',
    iqr: 'IQR',
    success: 'Success',
    points: 'pts',
    consistency: 'Consistency',
    highVariability: 'High variability',
    tighterSpread: 'Tighter spread',
    trend: 'Trend',
    whatChanged: 'What changed',
    keySummary: 'Key summary',
    selectedAttempt: 'Selected attempt',
    openAttemptDetails: 'Open attempt details',
    phaseAnalysis: 'Phase analysis',
    phaseScores: 'Phase scores',
    rating: 'Rating',
    backToClip: 'Back to clip',
    weightingAria: 'Analysis weighting',
    splitByAria: 'Split progression by',
    phaseChartView: 'Phase chart view',
    repAttempts: 'Representative attempts',
    noAttemptsInView: 'No attempts in this view.',
    noAttemptsMatch: 'No {{code}} attempts match the filters.',
    totalAttempts: 'total attempts',
    scored: 'scored',
    missing: 'missing',
    noAttemptsWithFilters: 'No attempts match these filters.',
    closeAttempt: 'Close attempt details',
    mediaFor: 'Media for {{code}} attempt {{id}}',
    noLinkedAsset: 'No linked Photos asset',
    sessionN: 'Session {{id}}',
    recent5Median: 'Recent 5 median',
    fromRolling: 'From rolling median',
    openPrevious: 'Open previous attempt',
    openNext: 'Open next attempt',
    next: 'Next',
    openSession: 'Open session',
    personalReviewPriority: 'Personal review priority',
    pdvHistoryAlt: 'Personal Dive Value history for {{count}} attempts. Values range from {{min}} to {{max}}.',
    pdvPoint: '{{date}}: Personal Dive Value {{value}}, {{signal}}',
    pdvScale: 'Personal Dive Value / 100',
    pdvNotable: 'Notable Personal Dive Value attempts',
    mostNotable: 'Most notable in this view',
    historicalSignal: 'Historical signal',
    representatives: 'Representative attempts',
    openRepGallery: 'Open representative gallery',
    noTypicalRep: 'No Typical representative selected.',
    noBandRep: 'No {{band}} representative selected.',
    noPoster: 'No poster ready',
    play: 'Play',
    openAttempt: 'Open attempt',
    findReplacement: 'Find replacement',
  },
  // ---- Added by the UX fixes (2026-11, feedback reliability) ------------
  routeError: {
    title: 'This page could not open.',
    body: 'Loading the app interrupted itself. Reload to try again.',
    reload: 'Reload',
  },
} as const

export default en
